import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Egg, PawPrint, Users, CalendarDays, Home, Loader2, CheckCircle2, Images, Baby, CalendarClock, Share2 } from "lucide-react";
import { LitterUpdatesFeed } from "./LitterUpdatesFeed";
import { BabiesGallery } from "./BabiesGallery";
import { BookSlotDialog } from "./BookSlotDialog";
import { LitterShareDialog } from "../LitterShareDialog";
import { api, errText, photoUrl } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { useLanguage } from "../../context/LanguageContext";
import { statusLabel, STATUS_CLS, fmtDay, kindLabel, speciesLabel, dueIn } from "../../lib/litters";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";

export const JoinDialog = ({ litter, onClose, onJoined }) => {
  const { t } = useLanguage();
  const [pref, setPref] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setPref(""); setNote(""); }, [litter]);
  const join = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/litters/${litter.id}/waitlist`, { preference: pref, note });
      toast.success(`${t("lt_joined_toast")}${data.position}`);
      onJoined();
    } catch (e) { toast.error(errText(e)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={!!litter} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md bg-white" data-testid="waitlist-join-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-[#12234A]">{t("lt_join_title")}</DialogTitle>
          <DialogDescription className="text-slate-500">{t("lt_join_desc")}</DialogDescription>
        </DialogHeader>
        <Input value={pref} onChange={(e) => setPref(e.target.value)} maxLength={80} placeholder={t("lt_pref_ph")} data-testid="waitlist-preference" className="rounded-xl" />
        <Textarea value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} rows={3} placeholder={t("lt_note_ph")} data-testid="waitlist-note" className="rounded-xl" />
        <Button onClick={join} disabled={busy} data-testid="waitlist-join-confirm" className="rounded-xl bg-[#F5551E] hover:bg-[#E04812] text-white gap-1.5">{busy && <Loader2 size={15} className="animate-spin" />} {t("lt_join")}</Button>
      </DialogContent>
    </Dialog>
  );
};

const LitterTile = ({ l, mine, onJoin, onLeave, onFeed, onBabies, onBook, onShare }) => {
  const { t, lang } = useLanguage();
  const Icon = l.kind === "clutch" ? Egg : PawPrint;
  const canJoin = !mine && !l.my_entry && l.waitlist_open && !l.is_full;
  return (
    <article className="bg-white rounded-2xl border border-slate-200/80 overflow-hidden flex flex-col" data-testid={`litter-card-${l.id}`}>
      {l.photo ? <img src={photoUrl(l.photo)} alt="" className="h-40 w-full object-cover" /> : (
        <div className="h-24 bg-gradient-to-br from-purple-50 to-[#FFF4F0] flex items-center justify-center"><Icon size={34} className="text-purple-300" /></div>
      )}
      <div className="p-4 flex-1 flex flex-col">
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={() => onShare(l)} aria-label={t("sh_btn")} data-testid={`litter-share-btn-${l.id}`} className="order-last ml-auto text-slate-400 hover:text-[#F5551E]"><Share2 size={15} /></button>
          <span className="text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 inline-flex items-center gap-1" data-testid={`litter-kind-${l.id}`}><Icon size={11} /> {kindLabel(l.kind, t)}</span>
          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${STATUS_CLS[l.shown_status || l.status]}`}>{statusLabel(l.shown_status || l.status, l.kind, t)}</span>
          {dueIn(l, lang) && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#F5551E] text-white" data-testid={`board-countdown-${l.id}`}>{dueIn(l, lang)}</span>}
        </div>
        <h3 className="font-display font-bold text-[#12234A] mt-2 leading-snug">{l.title || `${l.breed} · ${kindLabel(l.kind, t)}`}</h3>
        <p className="text-xs text-slate-500">{speciesLabel(l.species, lang)} · {l.breed}{l.expected_count ? ` · ${l.expected_count} ${t("lt_expected_n")}` : ""}</p>
        <div className="mt-2 space-y-1 text-xs text-slate-600">
          {l.expected_date && <p className="flex items-center gap-1.5"><CalendarDays size={13} className="text-slate-400" /> {l.arrived ? statusLabel("arrived", l.kind, t) : t(l.kind === "clutch" ? "lt_hatching" : "lt_due")} {fmtDay(l.expected_date, lang)}</p>}
          {l.ready_date && <p className="flex items-center gap-1.5"><Home size={13} className="text-slate-400" /> {t("lt_ready")} {fmtDay(l.ready_date, lang)}</p>}
          {l.price_note && <p className="font-semibold text-[#F5551E]">{l.price_note}</p>}
        </div>
        {l.description && <p className="text-sm text-slate-600 mt-2 line-clamp-3">{l.description}</p>}
        {(l.updates_count > 0 || l.babies_count > 0) && (
          <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1">
            {l.babies_count > 0 && <button onClick={() => onBabies(l)} data-testid={`litter-babies-btn-${l.id}`} className="text-xs font-semibold text-purple-700 hover:underline inline-flex items-center gap-1"><Baby size={13} /> {t("bb_view")} ({l.babies_count})</button>}
            {l.updates_count > 0 && <button onClick={() => onFeed(l)} data-testid={`litter-feed-btn-${l.id}`} className="text-xs font-semibold text-purple-700 hover:underline inline-flex items-center gap-1"><Images size={13} /> {t("lu_view")} ({l.updates_count})</button>}
          </div>
        )}
        {l.my_entry && (
          <button onClick={() => onBook(l)} data-testid={`litter-book-${l.id}`} className="mt-2 self-start text-xs font-semibold text-[#F5551E] hover:underline inline-flex items-center gap-1"><CalendarClock size={13} /> {t("sl_book_btn")}</button>
        )}
        {l.my_entry?.reserved && <p className="mt-2 text-[11px] font-semibold text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-2 py-1" data-testid={`litter-reserved-${l.id}`}>{t("wr_your_spot")}{l.my_entry.deposit_note ? ` · ${l.my_entry.deposit_note}` : ""}</p>}
        <div className="mt-auto pt-3 flex items-center justify-between gap-2">
          <span className="text-xs text-slate-500 inline-flex items-center gap-1" data-testid={`litter-count-${l.id}`}><Users size={13} /> {l.waitlist_count} {t("lt_waiting")}</span>
          {l.my_entry ? (
            <span className="flex items-center gap-2">
              <span className="text-xs font-semibold text-emerald-700 inline-flex items-center gap-1" data-testid={`litter-joined-${l.id}`}><CheckCircle2 size={14} /> {t("lt_you_are")}{l.my_entry.position}</span>
              <button onClick={() => onLeave(l)} data-testid={`litter-leave-${l.id}`} className="text-[11px] text-slate-400 hover:text-red-600">{t("lt_leave")}</button>
            </span>
          ) : canJoin ? (
            <Button size="sm" onClick={() => onJoin(l)} data-testid={`litter-join-${l.id}`} className="rounded-lg h-8 bg-[#F5551E] hover:bg-[#E04812] text-white">{t("lt_join")}</Button>
          ) : !mine && <span className="text-xs text-slate-400">{t(l.is_full ? "lt_full" : "lt_closed")}</span>}
        </div>
      </div>
    </article>
  );
};

export function LitterBoard({ breederId, mine }) {
  const { user, setAuthOpen } = useAuth();
  const { t } = useLanguage();
  const [items, setItems] = useState(null);
  const [joining, setJoining] = useState(null);
  const [feed, setFeed] = useState(null);
  const [babies, setBabies] = useState(null);
  const [booking, setBooking] = useState(null);
  const [sharing, setSharing] = useState(null);
  const load = () => api.get(`/users/${breederId}/litters`).then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, [breederId, user?.user_id]); // eslint-disable-line react-hooks/exhaustive-deps
  const onJoin = (l) => (user ? setJoining(l) : setAuthOpen(true));
  const onLeave = async (l) => {
    try { await api.delete(`/litters/${l.id}/waitlist`); toast.success(t("lt_left_toast")); load(); } catch (e) { toast.error(errText(e)); }
  };
  if (items === null) return null;
  if (!items.length) return (
    <div className="bg-white rounded-2xl border border-slate-200/80 p-8 text-center text-slate-400 text-sm" data-testid="litter-board-empty">
      {t("lt_empty")}{mine && <> <a href="/dashboard?tab=litters" className="text-[#F5551E] font-semibold hover:underline">{t("lt_announce_link")}</a></>}
    </div>
  );
  return (
    <>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="litter-board">
        {items.map((l) => <LitterTile key={l.id} l={l} mine={mine} onJoin={onJoin} onLeave={onLeave} onFeed={setFeed} onBabies={setBabies} onBook={setBooking} onShare={setSharing} />)}
      </div>
      <LitterUpdatesFeed litter={feed} onClose={() => setFeed(null)} />
      <BabiesGallery litter={babies} onClose={() => setBabies(null)} />
      <BookSlotDialog litter={booking} onClose={() => setBooking(null)} />
      <LitterShareDialog litter={sharing} onClose={() => setSharing(null)} />
      <JoinDialog litter={joining} onClose={() => setJoining(null)} onJoined={() => { setJoining(null); load(); }} />
    </>
  );
}
